document.addEventListener('alpine:init', () => {
  Alpine.data('predictiveSearch', () => ({
    open: false,
    query: '',
    loading: false,
    metaobjectsData: null,
    metaobjectsLoaded: false,
    metaobjectsLoading: false,
    storefrontConfig: null,

    // New state for optimizations
    searchController: null,
    selectedIndex: -1,
    results: {
      products: [],
      articles: [],
      pages: [],
      collections: [],
      queries: [],
      designers: [],
      projects: [],
    },

    init() {
      // Load storefront config from global object
      if (window.predictiveSearchConfig) {
        this.storefrontConfig = window.predictiveSearchConfig;
      } else {
        console.error('Predictive Search Config not found.');
      }

      // Check cache for metaobjects
      try {
        const cached = sessionStorage.getItem('predictive_search_metaobjects_v2');
        if (cached) {
          const data = JSON.parse(cached);
          // Check expiry (1 hour)
          if (Date.now() - data.timestamp < 3600000) {
            this.metaobjectsData = data.payload;
            this.metaobjectsLoaded = true;
          } else {
            sessionStorage.removeItem('predictive_search_metaobjects_v2');
          }
        }
      } catch (e) {
        console.error('Cache read error:', e);
      }
    },

    get hasResults() {
      return (
        this.results.products.length > 0 ||
        this.results.articles.length > 0 ||
        this.results.pages.length > 0 ||
        this.results.collections.length > 0 ||
        this.results.queries.length > 0 ||
        this.results.designers.length > 0 ||
        this.results.projects.length > 0
      );
    },

    // Flatten results for keyboard navigation
    get allResults() {
      const list = [];
      // Queries
      if (this.results.queries && this.results.queries.length) {
        this.results.queries.forEach((i) => list.push({ type: 'query', id: i.text, text: i.text, url: null }));
      }
      // Products
      if (this.results.products && this.results.products.length) {
        this.results.products.slice(0, 4).forEach((i) => list.push({ type: 'product', id: i.id, url: i.url }));
      }
      // Designers
      if (this.results.designers && this.results.designers.length) {
        this.results.designers.slice(0, 6).forEach((i) => list.push({ type: 'designer', id: i.url, url: i.url }));
      }
      // Projects
      if (this.results.projects && this.results.projects.length) {
        this.results.projects.slice(0, 6).forEach((i) => list.push({ type: 'project', id: i.url, url: i.url }));
      }
      // Articles
      if (this.results.articles && this.results.articles.length) {
        this.results.articles.slice(0, 3).forEach((i) => list.push({ type: 'article', id: i.id, url: i.url }));
      }
      // Collections
      if (this.results.collections && this.results.collections.length) {
        this.results.collections.forEach((i) => list.push({ type: 'collection', id: i.id, url: i.url }));
      }
      // Pages
      if (this.results.pages && this.results.pages.length) {
        this.results.pages.forEach((i) => list.push({ type: 'page', id: i.id, url: i.url }));
      }
      return list;
    },

    nextResult() {
      if (this.allResults.length === 0) return;
      this.selectedIndex = this.selectedIndex + 1;
      if (this.selectedIndex >= this.allResults.length) this.selectedIndex = 0;
    },

    prevResult() {
      if (this.allResults.length === 0) return;
      this.selectedIndex = this.selectedIndex - 1;
      if (this.selectedIndex < 0) this.selectedIndex = this.allResults.length - 1;
    },

    goToResult() {
      if (this.selectedIndex >= 0 && this.allResults[this.selectedIndex]) {
        const res = this.allResults[this.selectedIndex];
        if (res.type === 'query') {
          this.selectSuggestion(res.text);
        } else if (res.url) {
          window.location.href = res.url;
        }
      } else if (this.query.trim().length > 0) {
        // Default enter behavior: submit search
        if (this.storefrontConfig && this.storefrontConfig.routes && this.storefrontConfig.routes.search_url) {
             window.location.href = `${this.storefrontConfig.routes.search_url}?q=${encodeURIComponent(this.query)}`;
        }
      }
    },

    isSelected(type, id) {
      const current = this.allResults[this.selectedIndex];
      return current && current.type === type && current.id === id;
    },

    toggle() {
      this.open = !this.open;
      if (this.open) {
        document.body.style.overflow = 'hidden';
        this.$nextTick(() => {
          this.$refs.searchInput?.focus();
        });
      } else {
        document.body.style.overflow = '';
        this.selectedIndex = -1;
      }
    },

    close() {
      this.open = false;
      this.query = '';
      this.selectedIndex = -1;
      document.body.style.overflow = '';
      this.resetResults();
    },

    clearQuery() {
      this.query = '';
      this.resetResults();
      this.$refs.searchInput?.focus();
    },

    resetResults() {
      this.results = {
        products: [],
        articles: [],
        pages: [],
        collections: [],
        queries: [],
        designers: [],
        projects: [],
      };
      this.selectedIndex = -1;
    },

    selectSuggestion(text) {
      this.query = text;
      this.search();
    },

    // Normalize Turkish characters for search
    normalizeTurkish(str) {
      if (!str) return '';
      const map = {
        İ: 'i',
        I: 'i',
        ı: 'i',
        Ğ: 'g',
        ğ: 'g',
        Ü: 'u',
        ü: 'u',
        Ş: 's',
        ş: 's',
        Ö: 'o',
        ö: 'o',
        Ç: 'c',
        ç: 'c',
      };
      return str.replace(/[İIıĞğÜüŞşÖöÇç]/g, (c) => map[c]).toLowerCase();
    },

    parseRichText(value) {
      if (!value) return '';
      try {
        if (value.includes('{"type":"root"')) {
          const json = JSON.parse(value);
          const extractText = (node) => {
            if (typeof node === 'string') return node;
            if (node.value) return node.value;
            if (node.children) return node.children.map(extractText).join(' ');
            return '';
          };
          return extractText(json);
        }
        return value;
      } catch (e) {
        return value;
      }
    },

    filterMetaobjects(items, query) {
      if (!items || !query) return [];
      const normalizedQuery = this.normalizeTurkish(query.trim());
      return items.filter((item) => {
        const name = this.normalizeTurkish(item.name || '');
        const description = this.normalizeTurkish(item.description || '');
        return name.includes(normalizedQuery) || description.includes(normalizedQuery);
      });
    },

    async fetchMetaobjectsFromAPI(type, config) {
      if (!this.storefrontConfig) return [];

      const query = `
        query GetMetaobjects($type: String!, $first: Int!) {
          metaobjects(type: $type, first: $first) {
            nodes {
              handle
              fields {
                key
                value
                reference {
                  ... on MediaImage {
                    image {
                      url(transform: { maxWidth: 200 })
                    }
                    previewImage {
                      url(transform: { maxWidth: 200 })
                    }
                  }
                  ... on GenericFile {
                    url
                  }
                  ... on Video {
                    previewImage {
                      url(transform: { maxWidth: 200 })
                    }
                  }
                  ... on Model3d {
                    previewImage {
                      url(transform: { maxWidth: 200 })
                    }
                  }
                }
              }
            }
          }
        }
      `;

      try {
        const response = await fetch(`https://${this.storefrontConfig.shopDomain}/api/2025-01/graphql.json`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': this.storefrontConfig.storefrontToken,
          },
          body: JSON.stringify({
            query,
            variables: { type: config.type, first: 250 },
          }),
        });

        if (!response.ok) throw new Error(`Storefront API error: ${response.status}`);
        const data = await response.json();
        if (data.errors) {
          console.error('GraphQL errors:', data.errors);
          return [];
        }

        return (data.data?.metaobjects?.nodes || []).map((node) => {
          const fields = {};
          node.fields.forEach((field) => {
            if (field.reference?.image?.url) {
              fields[field.key] = field.reference.image.url;
            } else if (field.reference?.previewImage?.url) {
              fields[field.key] = field.reference.previewImage.url;
            } else if (field.reference?.url) {
              fields[field.key] = field.reference.url;
            } else {
              fields[field.key] = field.value;
            }
          });

          let imageUrl = fields[config.imageField];
          if (typeof imageUrl !== 'string' || imageUrl.startsWith('gid://') || !imageUrl.startsWith('http')) {
            imageUrl = null;
          }

          return {
            name: fields[config.nameField] || node.handle,
            url: config.urlPrefix + node.handle,
            image: imageUrl,
            description: config.descriptionField ? this.parseRichText(fields[config.descriptionField] || '') : '',
          };
        });
      } catch (error) {
        console.error(`Failed to fetch ${type} from Storefront API:`, error);
        return [];
      }
    },

    async loadMetaobjects() {
      if (this.metaobjectsLoaded || this.metaobjectsLoading || !this.storefrontConfig) {
        return;
      }

      this.metaobjectsLoading = true;

      try {
        const types = this.storefrontConfig.metaobjectTypes;
        const [designers, projects] = await Promise.all([
          this.fetchMetaobjectsFromAPI('designers', types.designers),
          this.fetchMetaobjectsFromAPI('projects', types.projects),
        ]);

        this.metaobjectsData = { designers, projects };
        this.metaobjectsLoaded = true;

        // Cache results
        try {
          sessionStorage.setItem(
            'predictive_search_metaobjects_v2',
            JSON.stringify({
              timestamp: Date.now(),
              payload: this.metaobjectsData,
            })
          );
        } catch (e) {
          console.error('Cache write error:', e);
        }

        if (this.query && this.query.trim().length >= 2) {
          this.search();
        }
      } catch (error) {
        console.error('Failed to load metaobjects:', error);
        this.metaobjectsData = { designers: [], projects: [] };
      } finally {
        this.metaobjectsLoading = false;
      }
    },

    async search() {
      // Reset selection on new search
      this.selectedIndex = -1;

      if (!this.query || this.query.trim().length < 2) {
        this.resetResults();
        return;
      }

      this.loading = true;

      // Abort previous request
      if (this.searchController) {
        this.searchController.abort();
      }
      this.searchController = new AbortController();

      try {
        if (!this.metaobjectsLoaded && !this.metaobjectsLoading) {
          this.loadMetaobjects();
        }

        const params = new URLSearchParams({
          q: this.query,
          'resources[type]': 'product,article,page,collection,query',
          'resources[limit]': '10',
          'resources[limit_scope]': 'each',
        });

        // Fetch with AbortSignal
         if(this.storefrontConfig && this.storefrontConfig.routes && this.storefrontConfig.routes.predictive_search_url) {
            const response = await fetch(`${this.storefrontConfig.routes.predictive_search_url}?${params.toString()}`, {
                headers: { Accept: 'application/json' },
                signal: this.searchController.signal,
            });

            if (!response.ok) throw new Error('Search request failed');
            const data = await response.json();

            const filteredDesigners = this.filterMetaobjects(this.metaobjectsData?.designers || [], this.query);
            const filteredProjects = this.filterMetaobjects(this.metaobjectsData?.projects || [], this.query);

            this.results = {
                products: data.resources?.results?.products || [],
                articles: data.resources?.results?.articles || [],
                pages: data.resources?.results?.pages || [],
                collections: data.resources?.results?.collections || [],
                queries: data.resources?.results?.queries || [],
                designers: filteredDesigners,
                projects: filteredProjects,
            };

            this.loading = false;
         }
      } catch (error) {
        if (error.name === 'AbortError') return; // Ignore aborted requests
        console.error('Predictive search error:', error);

        const filteredDesigners = this.filterMetaobjects(this.metaobjectsData?.designers || [], this.query);
        const filteredProjects = this.filterMetaobjects(this.metaobjectsData?.projects || [], this.query);

        this.results = {
          products: [],
          articles: [],
          pages: [],
          collections: [],
          queries: [],
          designers: filteredDesigners,
          projects: filteredProjects,
        };
        this.loading = false;
      }
    },
  }));
});
